import { Outlet, createFileRoute, linkOptions } from '@tanstack/react-router'
import type { TableMeta } from '@tanstack/react-table'
import { useAtomValue } from 'jotai'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type CoverageByCriteriaRequest,
  type CoverageResponse,
  CoverageSortingCriteria,
  type CoveragesGroupInput,
  type DateRange,
  DynamicStats,
} from 'types'

import DateRangeFilterDialog from '@/components/filters/date-range-filter-dialog'
import {
  useDynamicStats,
  useDynamicStatsOldestComputedAt as useDynamicStatsLastUpdate,
} from '@/stores/dynamic-stats.atom'
import { storePageSearch } from '@/stores/page-search.atom'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'

import CoverageByCriteria from '@/components/coverage/coverage-by-criteria/coverage-by-criteria'
import {
  type AnalysisFilters,
  analysisFiltersSchema,
} from '../-components/analysis-filters.schema'
import AnalysisRightBar from '../-components/analysis-right-bar'
import SectionHeader from '../../-components/section-layout/section-header'
import CoveragesStatsComponent from './-components/coverages-stats'

type Counts = Record<CoverageSortingCriteria, number>

const DEFAULT_COUNTS = Object.fromEntries(
  Object.values(CoverageSortingCriteria).map((criteria) => [criteria, 5]),
) as Counts

export const Route = createFileRoute('/app/analysis/coverages')({
  component: RouteComponent,
  validateSearch: analysisFiltersSchema,
  beforeLoad: ({ search, cause, location }) => {
    storePageSearch(
      Route.fullPath,
      { to: Route.fullPath, search },
      location,
      cause,
    )
  },
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const portfolioUserUuid = useAtomValue(portfolioFilterAtom)
  const divToExportRef = useRef(null)
  const [counts, setCounts] = useState<Counts>(DEFAULT_COUNTS)

  const setFilters = (filters: AnalysisFilters) =>
    navigate({ search: (prev) => ({ ...prev, ...filters }) })

  const coveragesGroupStatsInput: CoveragesGroupInput = {
    filters: {
      ...search,
      portfolioUserUuid,
    },
    items: [{ criteria: 'none' }],
  }

  const stockStats = useDynamicStats(
    DynamicStats.CoveragesGroup,
    coveragesGroupStatsInput,
  )
  const flowStatsInput: CoveragesGroupInput = {
    ...coveragesGroupStatsInput,
    filters: { ...coveragesGroupStatsInput.filters, flowOnly: true },
  }
  const flowStats = useDynamicStats(DynamicStats.CoveragesGroup, flowStatsInput)
  const lastUpdate = useDynamicStatsLastUpdate([stockStats, flowStats])

  const setDateRangeFilter = (dateRange: DateRange | undefined) =>
    navigate({ search: (prev) => ({ ...prev, dateRange }) })

  const coverageByCriteriaFilters: CoverageByCriteriaRequest = {
    ...search,
    counts,
    portfolioUserUuid,
  }

  return (
    <div className="flex w-full grow flex-col divide-y overflow-x-hidden">
      <SectionHeader
        isSecondary
        title={t('analysis.coverages.title')}
        subtitle={lastUpdate}
      >
        <AnalysisRightBar
          divToExportRef={divToExportRef}
          filters={search}
          setFilters={setFilters}
          filename="coverages-analysis"
        />
      </SectionHeader>
      <div
        className="flex h-full flex-col gap-2 overflow-y-auto p-2"
        ref={divToExportRef}
      >
        <CoveragesStatsComponent {...coveragesGroupStatsInput} />
        <DateRangeFilterDialog
          value={search.dateRange}
          onChange={setDateRangeFilter}
        />
        <CoverageByCriteria
          counts={counts}
          setCounts={setCounts}
          filters={coverageByCriteriaFilters}
          rowLink={rowLink}
        />
      </div>
      <Outlet />
    </div>
  )
}

const rowLink: TableMeta<CoverageResponse>['rowLink'] = (row) =>
  linkOptions({
    to: '/app/analysis/coverages/$uuid',
    params: { uuid: row.uuid },
  })
